import { CaretRight } from "@phosphor-icons/react";
import classNames from "classnames";
import * as React from "react";
import styles from "./ReadMoreLink.module.css";
import { lerp } from "../utils/animations";
import { LERP_FACTOR } from "../utils/constants";

export interface ReadMoreLinkProps
  extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  href: string;
  children?: React.ReactNode;
  darkMode?: boolean;
  forceHover?: boolean;
  as?: React.ElementType;
}

const Chevron = () => (
  <span className={styles.chevronWrapper}>
    <CaretRight className={styles.chevronOut} size={14} weight="bold" />
    <CaretRight className={styles.chevronIn} size={14} weight="bold" />
  </span>
);

const Glow = () => (
  <div className={styles.glowContainer}>
    <div className={styles.glowWrapper}>
      <div className={styles.glowTracker}>
        <div className={styles.glowInner}>
          <div className={styles.glowBlob1} />
          <div className={styles.glowBlob2} />
        </div>
      </div>
    </div>
  </div>
);

export const ReadMoreLink: React.FC<ReadMoreLinkProps> = ({
  href,
  children = "Read more",
  className,
  darkMode = false,
  forceHover = false,
  as: Component = "a",
  ...props
}) => {
  const linkRef = React.useRef<HTMLElement>(null);
  const targetPos = React.useRef({ x: 0, y: 0 });
  const outerPos = React.useRef({ x: 0, y: 0 });
  const currentScale = React.useRef(0.5);
  const animationRef = React.useRef<number | null>(null);
  const isHovering = React.useRef(false);
  const fillPhaseComplete = React.useRef(false);
  const fillStartTime = React.useRef<number | null>(null);
  const fillDuration = 400;

  const animate = React.useCallback(() => {
    if (!linkRef.current) return;

    const iconCircle = linkRef.current.querySelector(
      `.${styles.iconCircle}`,
    ) as HTMLElement | null;
    if (!iconCircle) return;

    const rect = iconCircle.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const now = Date.now();
    const fillElapsed = fillStartTime.current ? now - fillStartTime.current : 0;

    if (!fillPhaseComplete.current && fillElapsed < fillDuration) {
      const fillProgress = Math.min(fillElapsed / fillDuration, 1);
      const easedProgress =
        fillProgress < 0.5
          ? 2 * fillProgress * fillProgress
          : 1 - Math.pow(-2 * fillProgress + 2, 2) / 2;
      const fillTargetScale = 1.5;
      currentScale.current = lerp(0.2, fillTargetScale, easedProgress);

      outerPos.current.x = centerX;
      outerPos.current.y = centerY;

      if (fillProgress >= 1) {
        fillPhaseComplete.current = true;
      }
    } else {
      if (!fillPhaseComplete.current) {
        fillPhaseComplete.current = true;
      }

      outerPos.current.x = lerp(
        outerPos.current.x,
        targetPos.current.x,
        LERP_FACTOR.position,
      );
      outerPos.current.y = lerp(
        outerPos.current.y,
        targetPos.current.y,
        LERP_FACTOR.position,
      );

      const distanceFromCenter = Math.abs(outerPos.current.x - centerX);
      const maxDistance = rect.width / 2;
      const targetScale = 0.65 + (1 - distanceFromCenter / maxDistance) * 0.35;

      currentScale.current = lerp(
        currentScale.current,
        targetScale,
        LERP_FACTOR.scale,
      );
    }

    const shiftX = (outerPos.current.x - centerX) * 0.4;
    const shiftY = (outerPos.current.y - centerY) * 0.3;

    iconCircle.style.setProperty("--mouse-x-outer", `${shiftX}px`);
    iconCircle.style.setProperty("--mouse-y-outer", `${shiftY}px`);
    iconCircle.style.setProperty("--glow-scale", `${currentScale.current}`);

    if (isHovering.current) {
      animationRef.current = requestAnimationFrame(animate);
    }
  }, [fillDuration]);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!linkRef.current) return;
    const iconCircle = linkRef.current.querySelector(
      `.${styles.iconCircle}`,
    ) as HTMLElement | null;
    if (!iconCircle) return;
    const rect = iconCircle.getBoundingClientRect();
    targetPos.current.x = e.clientX - rect.left;
    targetPos.current.y = e.clientY - rect.top;
  };

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (forceHover) return; // Don't handle if forced hover
    isHovering.current = true;
    fillPhaseComplete.current = false;
    fillStartTime.current = Date.now();
    if (linkRef.current) {
      const iconCircle = linkRef.current.querySelector(
        `.${styles.iconCircle}`,
      ) as HTMLElement | null;
      if (iconCircle) {
        const rect = iconCircle.getBoundingClientRect();
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        targetPos.current = { x, y };
        outerPos.current = { x: centerX, y: centerY };
        currentScale.current = 0.2;
      }
    }
    animationRef.current = requestAnimationFrame(animate);
  };

  const handleMouseLeave = () => {
    if (forceHover) return; // Don't handle if forced hover
    isHovering.current = false;
    fillPhaseComplete.current = false;
    fillStartTime.current = null;
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
  };

  // Handle forceHover prop changes
  React.useEffect(() => {
    if (forceHover) {
      isHovering.current = true;
      fillPhaseComplete.current = false;
      fillStartTime.current = Date.now();
      if (linkRef.current) {
        const iconCircle = linkRef.current.querySelector(
          `.${styles.iconCircle}`,
        ) as HTMLElement | null;
        if (iconCircle) {
          const rect = iconCircle.getBoundingClientRect();
          const centerX = rect.width / 2;
          const centerY = rect.height / 2;
          const targetPosRef = targetPos.current;
          const outerPosRef = outerPos.current;
          // Reset to center
          targetPosRef.x = centerX;
          targetPosRef.y = centerY;
          outerPosRef.x = centerX;
          outerPosRef.y = centerY;
          currentScale.current = 0.2;
        }
      }
      animationRef.current = requestAnimationFrame(animate);
    } else {
      isHovering.current = false;
      fillPhaseComplete.current = false;
      fillStartTime.current = null;
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }
  }, [forceHover, animate]);

  React.useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <Component
      ref={linkRef}
      href={Component === "a" ? href : undefined}
      className={classNames(
        styles.readMoreLink,
        darkMode && styles.darkMode,
        forceHover && styles.forceHover,
        className,
      )}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...props}
    >
      <span className={styles.iconCircle}>
        <Glow />
        <Chevron />
      </span>
      <span className={styles.text}>{children}</span>
    </Component>
  );
};
